import { nameKey } from './names';
import { ARRAY_INDEX_ALL, isArrayAllIndex, isArrayVariable } from './types';
import { parseVariableRef } from './refs';
import { resolveVariableFrom } from './collect';

/** `{{name}}` tokens inside a value string (whitespace around the name allowed). */
const VAR_REF_RE = /\{\{\s*(?!=)([^}]+?)\s*\}\}/g;

/**
 * A contenteditable chip often saves a trailing line break, or one between
 * `{{folder}}` and `/rest`. That break is not part of the path: it prints as
 * a second line and stops Explorer from opening the value.
 */
export const joinPathLineBreaks = (text, { joinInternal = false } = {}) => {
  let next = String(text ?? '').replace(/[\r\n]+$/g, '');
  if (joinInternal) next = next.replace(/([^\s])\r?\n+(?=[\\/])/g, '$1');
  return next;
};

/**
 * Expand `{{other}}` / `{{other[1]}}` references inside a variable value,
 * using the same highest-wins scoped list as pills. Cycles, unknown names,
 * bare array refs, and out-of-range indices leave the original token intact.
 */
export const expandVariableValue = (
  raw,
  scoped = [],
  { maxDepth = 8, visiting = new Set() } = {}
) => {
  const source = String(raw ?? '');
  let text = source;
  const finish = (result) =>
    joinPathLineBreaks(result, { joinInternal: source.includes('{{') });
  if (maxDepth <= 0 || !text.includes('{{')) return finish(text);

  if (text.includes('{{=')) {
    const { expandMathTokensInValue } = require('../mathValueToken');
    text = expandMathTokensInValue(text, (ref) => {
      const parsed = parseVariableRef(ref);
      if (!parsed.name) return null;
      const visitKey = nameKey(parsed.ref);
      if (!visitKey || visiting.has(visitKey)) return null;
      const hit = resolveVariableFrom(scoped, parsed.name);
      if (!hit) return null;
      const nextVisiting = new Set(visiting);
      nextVisiting.add(visitKey);
      if (isArrayVariable(hit)) {
        if (parsed.index == null) return null;
        const values = Array.isArray(hit.values) ? hit.values : [];
        if (isArrayAllIndex(parsed.index)) return null;
        if (parsed.index < 1 || parsed.index > values.length) return null;
        return {
          ...hit,
          value: expandVariableValue(String(values[parsed.index - 1] ?? ''), scoped, {
            maxDepth: maxDepth - 1,
            visiting: nextVisiting,
          }),
        };
      }
      if (parsed.index != null) return null;
      return {
        ...hit,
        value: expandVariableValue(hit.value ?? '', scoped, {
          maxDepth: maxDepth - 1,
          visiting: nextVisiting,
        }),
      };
    });
    if (!text.includes('{{')) return finish(text);
  }

  return finish(text.replace(VAR_REF_RE, (match, rawName) => {
    const { name, index, ref } = parseVariableRef(rawName);
    if (!name) return match;
    const visitKey = nameKey(ref);
    if (!visitKey || visiting.has(visitKey)) return match;
    const hit = resolveVariableFrom(scoped, name);
    if (!hit) return match;

    let slot = '';
    if (isArrayVariable(hit)) {
      if (index == null) return match;
      const values = Array.isArray(hit.values) ? hit.values : [];
      const nextVisiting = new Set(visiting);
      nextVisiting.add(visitKey);
      if (isArrayAllIndex(index)) {
        return values
          .map((v) =>
            expandVariableValue(String(v ?? ''), scoped, {
              maxDepth: maxDepth - 1,
              visiting: nextVisiting,
            })
          )
          .filter((v) => v.trim() !== '')
          .join('\n');
      }
      if (index < 1 || index > values.length) return match;
      slot = String(values[index - 1] ?? '');
      return expandVariableValue(slot, scoped, {
        maxDepth: maxDepth - 1,
        visiting: nextVisiting,
      });
    }

    if (index != null) return match;
    slot = String(hit.value ?? '');

    const nextVisiting = new Set(visiting);
    nextVisiting.add(visitKey);
    return expandVariableValue(slot, scoped, {
      maxDepth: maxDepth - 1,
      visiting: nextVisiting,
    });
  }));
};

/**
 * Resolve a name / `name[1]` / `name[*]` ref and expand nested `{{refs}}`
 * in its value — what pills and previews should display.
 */
export const resolveVariableExpanded = (scoped, refRaw) => {
  const { name, index, ref } = parseVariableRef(refRaw);
  if (!name) return null;
  const hit = resolveVariableFrom(scoped, name);
  if (!hit) return null;

  if (isArrayVariable(hit)) {
    if (index == null) {
      return { ...hit, value: '', index: null, isArray: true, ref, all: false };
    }
    const values = Array.isArray(hit.values) ? hit.values : [];
    const visitKey = nameKey(ref);

    if (isArrayAllIndex(index)) {
      const value = values
        .map((v) =>
          expandVariableValue(String(v ?? ''), scoped, {
            visiting: visitKey ? new Set([visitKey]) : new Set(),
          })
        )
        .filter((v) => v.trim() !== '')
        .join('\n');
      return {
        ...hit,
        value,
        index: ARRAY_INDEX_ALL,
        isArray: true,
        all: true,
        ref,
      };
    }

    if (index < 1 || index > values.length) return null;
    return {
      ...hit,
      value: expandVariableValue(String(values[index - 1] ?? ''), scoped, {
        visiting: visitKey ? new Set([visitKey]) : new Set(),
      }),
      index,
      isArray: true,
      all: false,
      ref,
    };
  }

  if (index != null) return null;

  const key = nameKey(name);
  return {
    ...hit,
    value: expandVariableValue(hit.value ?? '', scoped, {
      visiting: key ? new Set([key]) : new Set(),
    }),
    index: null,
    isArray: false,
    all: false,
    ref: name,
  };
};
